/**
 * Aplicar uma proposta da consultoria de regras ("Pedir melhorias à IA",
 * 2026-10-21): cada tipo de proposta vira a chamada de cadastro que a
 * tela de Ajustes já faz — editar/criar/desativar premissa, mexer nos
 * termos, no modo ou na premissa de um tema, editar o critério de uma
 * mensagem-chave. NADA aqui fala com a rota `…/rule-reviews`: registrar
 * o desfecho é da tela, e só depois que o cadastro aceitou.
 *
 * Puro dos dois lados (sem React, o `api` entra por parâmetro), na forma
 * do `premise-revision.ts`: uma proposta por vez, a falha de uma volta
 * com o motivo e não derruba as outras. Texto vazio onde é edição é
 * recusa local — nada parte pro servidor.
 *
 * Termo de tema: o `after` que o servidor mandou é a lista inteira, mas
 * ela foi calculada sobre os termos que o tema tinha NA HORA da consulta;
 * aplicar com ela desfaria uma edição feita no meio-tempo. Por isso a
 * lista é refeita aqui sobre os termos de AGORA, e "o termo já está lá" /
 * "o termo já saiu" é sucesso (o cadastro já está como a proposta quer),
 * nunca erro.
 */

import type { PostSource, RuleReviewProposal, Theme } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { splitEntries } from "@/shared/text/split-entries";
import { foldText } from "@/shared/utils/text-search";
import type { DeterministicScope, ThemeClassificationMode } from "@/shared/vocabulary/theme-classification";
import type { RuleReviewTask } from "@/shared/vocabulary/rule-reviews";

/** O separador gravado entre as expressões de um tema, com o espaço que o
 *  servidor usa ao montar o `after`. */
const TERMS_JOIN = "; ";

export type RuleReviewApplyApi = {
  createPromptRule(
    token: string,
    payload: {
      brand_id: number;
      task: string;
      rule_type: string;
      source: PostSource;
      text: string;
      rule_review_id?: number;
    }
  ): Promise<unknown>;
  updatePromptRule(
    token: string,
    ruleId: number,
    payload: { text?: string; is_active?: boolean; rule_review_id?: number }
  ): Promise<unknown>;
  listThemes(token: string, opts?: { brandId?: number; includeInactive?: boolean }): Promise<Theme[]>;
  updateTheme(
    token: string,
    themeId: number,
    payload: {
      terms?: string | null;
      classification_mode?: ThemeClassificationMode;
      deterministic_scope?: DeterministicScope;
      detection_assumptions?: string | null;
      non_detection_assumptions?: string | null;
      rule_review_id?: number;
    }
  ): Promise<unknown>;
  updateKeyMessage(
    token: string,
    keyMessageId: number,
    payload: { rules?: string; rule_review_id?: number }
  ): Promise<unknown>;
};

export type ApplyResult =
  | { ok: true; /** O cadastro já estava como a proposta quer: nada foi escrito. */ note?: string }
  | { ok: false; message: string };

/** Os tipos cujo "depois" é um texto que a pessoa pode ajustar antes de
 *  aplicar. Nos outros ele é só leitura: o termo é a unidade da proposta
 *  de termo, e modo/desativação não têm texto. */
export function isAfterEditable(proposal: RuleReviewProposal): boolean {
  return (
    proposal.kind === "rule_update" ||
    proposal.kind === "rule_create" ||
    proposal.kind === "theme_update_premise" ||
    proposal.kind === "key_message_update_rules"
  );
}

/** A comparação de termo do servidor: sem acento, sem caixa, espaço colapsado. */
const termKey = (term: string) => foldText(term).replace(/\s+/g, " ").trim();

function failure(caught: unknown): ApplyResult {
  return {
    ok: false,
    message: errorMessage(caught, caught instanceof Error ? caught.message : "Não foi possível aplicar.")
  };
}

async function findTheme(
  api: RuleReviewApplyApi,
  token: string,
  themeId: number,
  brandId: number
): Promise<Theme | null> {
  const themes = await api.listThemes(token, { brandId, includeInactive: true });
  return themes.find(theme => theme.id === themeId) ?? null;
}

/**
 * Grava UMA proposta no cadastro. `after` é o texto que a pessoa deixou na
 * caixa (vale só nos tipos de `isAfterEditable`). Nunca lança.
 *
 * `ruleReviewId` é a consultoria de onde a proposta veio: vai em TODA
 * escrita de cadastro (`rule_review_id` no corpo), e é por ele que a trilha
 * de Auditoria diz "via consultoria #N". O servidor o confere contra a
 * empresa e o guarda só na trilha, nunca no cadastro.
 */
export async function applyRuleReviewProposal(
  api: RuleReviewApplyApi,
  token: string,
  task: RuleReviewTask,
  proposal: RuleReviewProposal,
  after: string,
  ruleReviewId: number
): Promise<ApplyResult> {
  const text = after.trim();
  const origin = { rule_review_id: ruleReviewId };
  try {
    switch (proposal.kind) {
      case "rule_update":
        if (!text) return { ok: false, message: "O texto da premissa está vazio." };
        await api.updatePromptRule(token, proposal.rule_id, { text, ...origin });
        return { ok: true };
      case "rule_deactivate":
        await api.updatePromptRule(token, proposal.rule_id, { is_active: false, ...origin });
        return { ok: true };
      case "rule_create":
        if (!text) return { ok: false, message: "O texto da premissa está vazio." };
        await api.createPromptRule(token, {
          brand_id: proposal.brand_id,
          task,
          rule_type: proposal.rule_type,
          source: proposal.source,
          text,
          ...origin
        });
        return { ok: true };
      case "theme_add_term":
      case "theme_remove_term": {
        const term = proposal.term.trim();
        if (!term) return { ok: false, message: "A proposta veio sem o termo." };
        const theme = await findTheme(api, token, proposal.theme_id, proposal.brand_id);
        if (!theme) return { ok: false, message: `O subtema #${proposal.theme_id} não foi encontrado.` };
        const current = splitEntries(theme.terms ?? "");
        const present = current.some(item => termKey(item) === termKey(term));
        if (proposal.kind === "theme_add_term") {
          if (present) return { ok: true, note: "O termo já estava no subtema." };
          await api.updateTheme(token, theme.id, {
            terms: [...current, term].join(TERMS_JOIN),
            ...origin
          });
          return { ok: true };
        }
        if (!present) return { ok: true, note: "O termo já tinha saído do subtema." };
        const remaining = current.filter(item => termKey(item) !== termKey(term));
        if (remaining.length === 0 && theme.classification_mode === "deterministic") {
          return {
            ok: false,
            message: "O subtema é só determinístico e ficaria sem termo; troque o modo antes."
          };
        }
        await api.updateTheme(token, theme.id, {
          terms: remaining.length > 0 ? remaining.join(TERMS_JOIN) : null,
          ...origin
        });
        return { ok: true };
      }
      case "theme_set_mode":
        await api.updateTheme(token, proposal.theme_id, {
          classification_mode: proposal.classification_mode,
          deterministic_scope: proposal.deterministic_scope,
          ...origin
        });
        return { ok: true };
      case "theme_update_premise":
        if (!text) return { ok: false, message: "O texto da premissa está vazio." };
        await api.updateTheme(token, proposal.theme_id, { [proposal.field]: text, ...origin });
        return { ok: true };
      case "key_message_update_rules":
        if (!text) return { ok: false, message: "O critério de aplicação está vazio." };
        await api.updateKeyMessage(token, proposal.key_message_id, { rules: text, ...origin });
        return { ok: true };
    }
  } catch (caught) {
    return failure(caught);
  }
}
